import { ProductCategoryItem } from 'components/shared/ProductAndClientSelector/ProductCategoryItem';
import { useTranslateProductType } from 'hooks/use-translate-payoff';
import type { ProductVariation } from 'models/productsReferential';
import { FC, useEffect, useMemo } from 'react';

export type ProductTypeSelectorProps = {
  products: readonly ProductVariation[];
  selectedAssetType?: string;
  selectedProductType?: string;
  onSelectProductType: (productType: string) => void;
};

export const ProductTypeSelector: FC<ProductTypeSelectorProps> = ({
  products,
  selectedAssetType,
  selectedProductType,
  onSelectProductType,
}) => {
  const productTypeOptions = useGetProductTypeOptions(products, selectedAssetType);

  useEffect(() => {
    if (!selectedProductType && productTypeOptions.length > 0) {
      onSelectProductType(productTypeOptions[0].value);
    }
  }, [selectedProductType, productTypeOptions, onSelectProductType]);

  if (selectedAssetType === 'Pinned') {
    return null;
  }

  return (
    <ul
      className="col-4 h-100 p-0 border-end overflow-y-auto scrollbar-sm list-unstyled mb-0"
      aria-label="Product types"
    >
      {productTypeOptions.map(({ value, label }) => (
        <li key={value}>
          <ProductCategoryItem
            className="ps-3 pe-0"
            isItemSelected={selectedProductType === value}
            onItemClick={() => onSelectProductType(value)}
          >
            <span className="text-wrap text-start">{label}</span>
            <span className="icon icon-sm text-muted mx-2" aria-hidden="true">
              keyboard_arrow_right
            </span>
          </ProductCategoryItem>
        </li>
      ))}
    </ul>
  );
};

const useGetProductTypeOptions = (productVariations: readonly ProductVariation[], selectedAssetType?: string) => {
  const translateProductType = useTranslateProductType();

  const filteredProductTypes = productVariations.filter(({ assetTypes }) =>
    assetTypes?.some(assetType => selectedAssetType === assetType),
  );

  return useMemo(() => {
    const distinctProductTypeOptions = filteredProductTypes
      .filter(({ productType }, pos, all) => all.findIndex(product => product.productType === productType) === pos)
      .map(({ productType, commercialProductType }) => ({
        label: translateProductType(productType, commercialProductType),
        value: productType,
      }));

    return distinctProductTypeOptions;
  }, [filteredProductTypes, translateProductType]);
};
